import { commitMutation, type Environment, fetchQuery } from 'react-relay';

import { IdentityUserAri } from '@atlassian/ari/identity';
import { PlatformSiteAri } from '@atlassian/ari/platform';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { AUTO_APPLY_STORAGE_KEY } from '../../constants/auto-apply';

import creationSettingsMutationGraphQL, {
	type creationSettingsMutationMutation,
	type SettingsCreationSettingsInput,
} from './__generated__/creationSettingsMutationMutation.graphql';
import creationSettingsQueryGraphQL, {
	type creationSettingsQueryQuery,
} from './__generated__/creationSettingsQueryQuery.graphql';

/**
 * Convert cloudId to tenantId ARI format
 */
function tenantIdToAri(cloudId: string): string {
	return PlatformSiteAri.create({ siteId: cloudId }).toString();
}

/**
 * Convert userId to ownerAri format
 */
function userIdToAri(userId: string): string {
	return IdentityUserAri.create({ userId }).toString();
}

/**
 * Get the current auto-apply state from localStorage
 */
export function getAutoApplyState(): boolean {
	try {
		const stored = window.localStorage.getItem(AUTO_APPLY_STORAGE_KEY);
		return stored !== null ? JSON.parse(stored) : true;
	} catch {
		return true; // fallback to default
	}
}

/**
 * Set the auto-apply state in localStorage
 */
export function setAutoApplyState(enabled: boolean): void {
	try {
		window.localStorage.setItem(AUTO_APPLY_STORAGE_KEY, JSON.stringify(enabled));
		window.dispatchEvent(new CustomEvent('autoApplyChanged'));
	} catch {
		// Silently fail if localStorage is unavailable
	}
}

/**
 * Get the current auto-apply state from backend service
 */
export function getAutoApplyStateFromBackend(
	environment: Environment,
	tenantId: string,
	ownerAri: string,
): Promise<boolean> {
	const variables = { tenantId, ownerAri };
	addSentryBreadcrumb(`getAutoApplyStateFromBackend: fetching for tenant=${tenantId}`);

	return fetchQuery<creationSettingsQueryQuery>(
		environment,
		creationSettingsQueryGraphQL,
		variables,
	)
		.toPromise()
		.then((response) => {
			// Reject if no response
			if (!response) {
				addSentryBreadcrumb('getAutoApplyStateFromBackend: no response received');
				throw new Error('Failed to fetch creation settings: No response');
			}

			// Return the autoApply value from backend, default to true if not found
			const autoApply = response?.settings_creationSettings?.autoApply ?? true;
			addSentryBreadcrumb(`getAutoApplyStateFromBackend: received autoApply=${autoApply}`);
			return autoApply;
		});
}

/**
 * Set the auto-apply state using backend service
 */
export function setAutoApplyStateToBackend(
	environment: Environment,
	tenantId: string,
	ownerAri: string,
	autoApply: boolean,
): Promise<boolean> {
	const input: SettingsCreationSettingsInput = {
		tenantId,
		ownerAri,
		autoApply,
	};
	addSentryBreadcrumb(
		`setAutoApplyStateToBackend: setting autoApply=${autoApply} for tenant=${tenantId}`,
	);

	return new Promise((resolve, reject) => {
		commitMutation<creationSettingsMutationMutation>(environment, {
			mutation: creationSettingsMutationGraphQL,
			variables: { input },
			onCompleted: (response, errors) => {
				const payload = response.settings_updateCreationSettings;
				if (errors || !payload) {
					addSentryBreadcrumb(`setAutoApplyStateToBackend: mutation failed with errors`);
					reject(errors || 'Failed to update creation settings');
				} else {
					addSentryBreadcrumb(
						`setAutoApplyStateToBackend: mutation succeeded, autoApply=${autoApply}`,
					);
					// Also update localStorage as cache/fallback
					setAutoApplyState(autoApply);
					resolve(autoApply);
				}
			},
			onError: (error) => {
				addSentryBreadcrumb(
					`setAutoApplyStateToBackend: mutation error - ${error?.message || 'unknown'}`,
				);
				// Reject so caller can handle the failure
				reject(error);
			},
		});
	});
}

/**
 * Toggle the auto-apply state (localStorage only - legacy)
 */
export function toggleAutoApplyState(): boolean {
	const currentState = getAutoApplyState();
	const newState = !currentState;
	setAutoApplyState(newState);
	return newState;
}

/**
 * Toggle the auto-apply state with backend persistence
 */
export async function toggleAutoApplyStateWithSession(
	environment: Environment,
	sessionData: { cloudId: string; userId: string | null },
): Promise<boolean> {
	addSentryBreadcrumb('toggleAutoApplyStateWithSession: toggling state');
	const currentState = await getAutoApplyStateWithSession(environment, sessionData);
	const newState = !currentState;
	addSentryBreadcrumb(`toggleAutoApplyStateWithSession: ${currentState} -> ${newState}`);

	// Update both backend and localStorage
	await setAutoApplyStateWithSession(environment, sessionData, newState);

	return newState;
}

/**
 * Toggle the auto-apply state with direct backend persistence
 */
export async function toggleAutoApplyStateToBackend(
	environment: Environment,
	tenantId: string,
	ownerAri: string,
): Promise<boolean> {
	addSentryBreadcrumb('toggleAutoApplyStateToBackend: toggling state');
	const currentState = await getAutoApplyStateFromBackend(environment, tenantId, ownerAri);
	const newState = !currentState;
	addSentryBreadcrumb(`toggleAutoApplyStateToBackend: ${currentState} -> ${newState}`);

	// Update both backend and localStorage
	await setAutoApplyStateToBackend(environment, tenantId, ownerAri, newState);

	return newState;
}

/**
 * Get auto-apply state using session data (for Confluence)
 */
export async function getAutoApplyStateWithSession(
	environment: Environment,
	sessionData: { cloudId: string; userId: string | null },
): Promise<boolean> {
	if (!sessionData.userId) {
		// Fallback to localStorage for anonymous users
		addSentryBreadcrumb('getAutoApplyStateWithSession: no userId, falling back to localStorage');
		return getAutoApplyState();
	}

	const tenantId = tenantIdToAri(sessionData.cloudId);
	const ownerAri = userIdToAri(sessionData.userId);
	addSentryBreadcrumb(`getAutoApplyStateWithSession: fetching for cloudId=${sessionData.cloudId}`);

	return getAutoApplyStateFromBackend(environment, tenantId, ownerAri);
}

/**
 * Set auto-apply state using session data (for Confluence)
 */
export async function setAutoApplyStateWithSession(
	environment: Environment,
	sessionData: { cloudId: string; userId: string | null },
	autoApply: boolean,
): Promise<boolean> {
	if (!sessionData.userId) {
		// Fallback to localStorage for anonymous users
		addSentryBreadcrumb(
			`setAutoApplyStateWithSession: no userId, using localStorage, autoApply=${autoApply}`,
		);
		setAutoApplyState(autoApply);
		return autoApply;
	}

	const tenantId = tenantIdToAri(sessionData.cloudId);
	const ownerAri = userIdToAri(sessionData.userId);
	addSentryBreadcrumb(
		`setAutoApplyStateWithSession: setting autoApply=${autoApply} for cloudId=${sessionData.cloudId}`,
	);

	return setAutoApplyStateToBackend(environment, tenantId, ownerAri, autoApply);
}

/**
 * React helper to load auto-apply state
 * Returns a function to call in useEffect
 */
export function createAutoApplyStateLoader(
	environment: Environment,
	tenantId: string,
	ownerAri: string,
	setState: (enabled: boolean) => void,
): () => Promise<void> {
	return async () => {
		const enabled = await getAutoApplyStateFromBackend(environment, tenantId, ownerAri);
		setState(enabled);
	};
}
